import { useEffect, useMemo, useState } from "react"

import type { EntityType } from "types/common.types"
import type {
  DeviceTelemetryParams,
  EntityTelemetry,
  PowerlineTelemetryParams,
  TowerTelemetryParams,
} from "types/telemetries.types"

import {
  useLazyDeviceTelemetryQuery,
  useLazyPowerlineTelemetryQuery,
  useLazyTowerTelemetryQuery,
} from "features/api"

/**
 * A rejected fetch leaves the chart on the data it already had. That is what the
 * request queue's superseded refusal wants — the range this chart was drawn for
 * has moved on — and it is what a plain failure already amounted to here, since
 * nothing but a fulfilled result ever reaches `setData`. Without a handler it
 * surfaced as an unhandled rejection on top of that.
 */
const keepCurrentData = () => {}

interface useEntityTelemetryProps {
  entity: EntityType
  entityId: number
  params: DeviceTelemetryParams | PowerlineTelemetryParams | TowerTelemetryParams
}

const useEntityTelemetry = ({ entity, entityId, params }: useEntityTelemetryProps) => {
  const [fetchDeviceTelemetryQuery, { isLoading: deviceTelemetryLoading }] =
    useLazyDeviceTelemetryQuery()
  const [fetchPowerlineTelemetryQuery, { isLoading: powerlineTelemetryLoading }] =
    useLazyPowerlineTelemetryQuery()
  const [fetchTowerTelemetryQuery, { isLoading: towerTelemetryLoading }] =
    useLazyTowerTelemetryQuery()
  const [data, setData] = useState<EntityTelemetry[]>([])

  useEffect(() => {
    switch (entity) {
      case "powerline":
        fetchPowerlineTelemetryQuery(
          {
            id: entityId,
            params: params as PowerlineTelemetryParams,
          },
          true,
        )
          .unwrap()
          .then((res) => setData(res))
          .catch(keepCurrentData)
        break
      case "tower":
        fetchTowerTelemetryQuery(
          {
            id: entityId,
            params: params as TowerTelemetryParams,
          },
          true,
        )
          .unwrap()
          .then((res) => setData(res))
          .catch(keepCurrentData)
        break
      case "group":
        break
      case "device":
      default:
        fetchDeviceTelemetryQuery(
          {
            id: entityId,
            params: params as DeviceTelemetryParams,
          },
          true,
        )
          .unwrap()
          .then((res) => setData(res))
          .catch(keepCurrentData)
    }
  }, [
    entity,
    entityId,
    fetchDeviceTelemetryQuery,
    fetchPowerlineTelemetryQuery,
    fetchTowerTelemetryQuery,
    params,
  ])

  const isLoading = useMemo(() => {
    switch (entity) {
      case "powerline":
        return powerlineTelemetryLoading
      case "tower":
        return towerTelemetryLoading
      case "group":
        return false
      case "device":
      default:
        return deviceTelemetryLoading
    }
  }, [deviceTelemetryLoading, entity, powerlineTelemetryLoading, towerTelemetryLoading])

  return {
    data,
    isLoading,
  }
}

export default useEntityTelemetry
